import { Form } from 'antd'
import CIMSFormItem from 'components/CIMSFormItem'
import { CIMSModal } from 'components/CIMSModal'
import { CIMSModalWrapperProps } from 'components/CIMSModal/types'
import CIMSCheckbox from 'components/CIMSCheckbox'
import { ExternalLinkInput, useCreateRfiResponseMutation } from 'schema'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { DefaultMutationHookOptions } from 'utils/defaults'
import SharePointUploader from 'components/SharePointUploader'
import { SharePointParentFolder } from 'shared'
import ExternalLinkEditor from 'components/ExternalLinkEditor'
import { useRFITrackerContext } from '../../../../RFITrackerContext'
import { useState } from 'react'
import CIMSTextArea from 'components/CIMSTextArea'
import { HelpMessage } from 'utils/constants'
import { useValidateWhitespaceProps } from 'components/CIMSTextArea/hooks/useValidateWhitespaceProps'

type DisciplineResponseModalValues = {
  text: string
  closed?: boolean
  externalLinks?: ExternalLinkInput[]
}

export type DisciplineResponseModalData = {
  requestForInformationId: string
  responseDisciplineId: string
  number: number
} & Pick<DisciplineResponseModalValues, 'externalLinks'>

const DisciplineResponseModal: React.FC<
  CIMSModalWrapperProps<DisciplineResponseModalData>
> = ({ visible, onClose, state }) => {
  const { refetch, metadata } = useRFITrackerContext()
  const [loading, setLoading] = useState<boolean>(false)
  const [submitResponse] = useCreateRfiResponseMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      createRFIResponse: {
        response: { createdUser },
      },
    }) =>
      simpleSuccessNotification(
        `Response successfully submitted by ${createdUser}`
      ),
  })
  const validateWhitespaceProps = useValidateWhitespaceProps({
    label: 'Response',
    required: true,
  })
  return (
    <CIMSModal
      title="Discipline Response"
      visible={visible}
      loading={loading}
      onCancel={onClose}
      initialFormValues={{
        text: '',
        externalLinks: state?.externalLinks,
      }}
      onOk={async ({
        text,
        closed,
        externalLinks,
      }: DisciplineResponseModalValues) => {
        setLoading(true)
        await submitResponse({
          variables: {
            createRfiResponseInput: {
              requestForInformationId: state!.requestForInformationId,
              responseDisciplineId: state!.responseDisciplineId,
              text,
              closed: closed ?? false,
              externalLinks,
            },
          },
        })
        setLoading(false)
        refetch()
        onClose()
      }}
    >
      <CIMSFormItem
        name="text"
        help={HelpMessage.RequestsForInformation.RFITracker.Response}
        {...validateWhitespaceProps}
      >
        <CIMSTextArea />
      </CIMSFormItem>
      <CIMSFormItem
        name="closed"
        label="Close Discipline's Response"
        help={
          HelpMessage.RequestsForInformation.RFITracker.CloseDisciplineResponse
        }
        valuePropName="checked"
      >
        <CIMSCheckbox />
      </CIMSFormItem>
      <Form.Item>
        <SharePointUploader
          parent={SharePointParentFolder.RequestForInformation}
          folder={state?.number.toString() ?? crypto.randomUUID()}
          sharePointResourceList={false}
        />
      </Form.Item>
      <CIMSFormItem name="externalLinks">
        <ExternalLinkEditor
          quickLinkOptions={{
            loading: metadata?.loading,
            quickLinks: metadata?.rfiQuickLinks,
          }}
        />
      </CIMSFormItem>
    </CIMSModal>
  )
}

export default DisciplineResponseModal
